import { inject, isDevMode } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, retry, timeout, throwError } from 'rxjs';
import { NotificationService } from '@core/services/notification.service';

const REQUEST_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 2;

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  const notificationService = inject(NotificationService);

  return next(request).pipe(
    timeout(REQUEST_TIMEOUT),
    retry({
      count: MAX_RETRIES,
      delay: (error, retryCount) => {
        // Only retry on 5xx or network errors
        if (error instanceof HttpErrorResponse && error.status >= 500) {
          if (isDevMode()) {
            console.warn(`[ErrorInterceptor] Retrying request (${retryCount}/${MAX_RETRIES}): ${request.url}`);
          }
          return new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
        if (error.status === 0) {
          // Network error
          return new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
        // Don't retry 4xx errors
        throw error;
      }
    }),
    catchError((error) => {
      const message = classifyHttpError(error);
      notificationService.showError(message);

      if (isDevMode()) {
        console.error(`[ErrorInterceptor] ${request.method} ${request.url}`, error);
      }

      return throwError(() => error);
    })
  );
};

function classifyHttpError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    switch (error.status) {
      case 0:
        return 'Erreur de connexion reseau. Verifiez votre connexion internet.';
      case 400:
        return 'Requete invalide. Verifiez les donnees saisies.';
      case 403:
        return 'Acces interdit. Vous n\'avez pas les permissions requises.';
      case 404:
        return 'Ressource introuvable.';
      case 409:
        return 'Conflit de donnees. Veuillez rafraichir et reessayer.';
      case 422:
        return 'Donnees invalides. Verifiez votre saisie.';
      case 429:
        return 'Trop de requetes. Veuillez patienter un moment.';
      case 500:
      case 502:
      case 503:
        return 'Erreur serveur. Veuillez reessayer dans quelques instants.';
      default:
        return `Erreur HTTP ${error.status}. Veuillez reessayer.`;
    }
  }
  if (error instanceof Error && error.name === 'TimeoutError') {
    return 'La requete a expire. Verifiez votre connexion et reessayer.';
  }
  return 'Une erreur inattendue est survenue.';
}
